import type { Meeting } from '@nexo/types';
import { AvatarStack, Button, EmptyState, Spinner, cn } from '@nexo/ui';
import { CalendarClock, CalendarPlus, Link2, Video } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { TeamHeader } from '../../layouts/TeamHeader';
import { useTeam } from '../../layouts/TeamContext';
import { formatDay, formatTime } from '../../lib/format';
import { useMeetings } from '../../lib/queries';
import { useMe } from '../auth/AuthProvider';
import { channelLabel } from '../channels/ChannelSidebar';
import { JoinMeetingButton, MeetingDialog, RsvpButtons } from './MeetingView';
import { useStartCall } from '../calls/CallControls';
import { ScheduleMeetingDialog } from './ScheduleMeetingDialog';
import { useInviteLink } from './useInviteLink';
import { meetingState } from './meetingUtils';

/** Upcoming meetings of the team, grouped by day. */
export function MeetingsPage() {
  const { team, channels } = useTeam();
  const me = useMe();
  const meetings = useMeetings(team.id);
  const [scheduling, setScheduling] = useState(false);
  const invite = useInviteLink();
  const startCall = useStartCall();
  const { t } = useTranslation();
  // Quick meetings happen in the first voice channel (or the first text channel).
  const room =
    channels.find((c) => c.mode === 'voice') ??
    channels.find((c) => c.kind === 'channel' && c.mode === 'text');
  const meetNow = async () => {
    if (!room) return;
    const meeting = await invite(room.id);
    if (meeting) await startCall(room.id, false);
  };
  const [params, setParams] = useSearchParams();
  const [picked, setPicked] = useState<string | null>(null);
  // Deep link from notifications: ?meeting=<id> (cleared when the dialog closes).
  const openId = picked ?? params.get('meeting');
  const closeMeeting = () => {
    setPicked(null);
    if (params.has('meeting')) {
      setParams(
        (p) => {
          p.delete('meeting');
          return p;
        },
        { replace: true },
      );
    }
  };

  const visible = (meetings.data ?? []).filter((m) => meetingState(m) !== 'ended');
  const groups = new Map<string, Meeting[]>();
  for (const meeting of visible) {
    const day = formatDay(meeting.startsAt);
    groups.set(day, [...(groups.get(day) ?? []), meeting]);
  }

  return (
    <>
      <TeamHeader
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              icon={<Video className="size-4" />}
              onClick={() => void meetNow()}
              disabled={!room}
              title={t('Crea una reunión ahora, entra y copia el enlace para invitar')}
            >
              {t('Reunión ahora')}
            </Button>
            <Button
              size="sm"
              icon={<CalendarPlus className="size-4" />}
              onClick={() => setScheduling(true)}
            >
              {t('Programar reunión')}
            </Button>
          </div>
        }
      >
        <CalendarClock className="size-[18px] shrink-0 text-muted" />
        <h1 className="text-[17px] font-semibold tracking-tight">{t('Reuniones')}</h1>
      </TeamHeader>
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        {meetings.isPending ? (
          <div className="flex justify-center py-10 text-muted">
            <Spinner />
          </div>
        ) : visible.length === 0 ? (
          <EmptyState
            icon={<CalendarClock className="size-6" />}
            title={t('No hay reuniones programadas')}
            description={t(
              'Programa una reunión en un canal de voz o de texto; los invitados recibirán un aviso y podrán confirmar su asistencia.',
            )}
            action={
              <Button
                icon={<CalendarPlus className="size-4" />}
                onClick={() => setScheduling(true)}
              >
                {t('Programar reunión')}
              </Button>
            }
          />
        ) : (
          [...groups.entries()].map(([day, list]) => (
            <section key={day} className="mb-6">
              <h2 className="mb-2 text-xs font-semibold tracking-wider text-subtle uppercase">
                {day}
              </h2>
              <ul className="flex flex-col gap-2">
                {list.map((meeting) => {
                  const state = meetingState(meeting);
                  const channel = channels.find((c) => c.id === meeting.channelId);
                  const invited = meeting.attendees.some((a) => a.id === me.id);
                  return (
                    <li
                      key={meeting.id}
                      className={cn(
                        'flex flex-wrap items-center gap-3 rounded-xl border bg-surface p-3 shadow-panel',
                        state === 'live' ? 'border-green-600/40' : 'border-border',
                        state === 'cancelled' && 'opacity-60',
                      )}
                    >
                      <div className="w-14 shrink-0 text-center">
                        <p className="text-sm font-semibold tabular-nums">
                          {formatTime(meeting.startsAt)}
                        </p>
                        <p className="text-[11px] text-subtle">
                          {t('{{n}} min', { n: meeting.durationMinutes })}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPicked(meeting.id)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <p
                          className={cn(
                            'truncate font-medium',
                            state === 'cancelled' && 'line-through',
                          )}
                        >
                          {meeting.title}
                          {meeting.guestUrl && (
                            <Link2 className="ml-1.5 inline size-3.5 text-subtle" />
                          )}
                        </p>
                        <p className="truncate text-xs text-muted">
                          {state === 'live' && (
                            <span className="font-semibold text-green-600">{t('En curso')} · </span>
                          )}
                          {state === 'cancelled' && (
                            <span className="font-semibold text-danger">{t('Cancelada')} · </span>
                          )}
                          {channel
                            ? `${channel.mode === 'voice' ? '🔊 ' : '#'}${channelLabel(channel)}`
                            : ''}{' '}
                          · {t('organiza {{name}}', { name: meeting.createdBy.displayName })}
                        </p>
                      </button>
                      <AvatarStack people={meeting.attendees} max={4} size="xs" />
                      {state !== 'cancelled' && invited && <RsvpButtons meeting={meeting} />}
                      <JoinMeetingButton meeting={meeting} />
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </div>
      <ScheduleMeetingDialog open={scheduling} onClose={() => setScheduling(false)} />
      <MeetingDialog meetingId={openId} onClose={closeMeeting} />
    </>
  );
}
